/**
 * Client for the standalone requirement-coverage API (the `server/` app that
 * ships with the dashboard prototype, default port 8787).
 *
 * Point REACT_APP_COVERAGE_API_BASE_URL at that server to see live numbers.
 * When it is unreachable, CoverageProvider serves the demo data instead, so
 * the dashboard renders identically with or without it running.
 */
const trimTrailingSlash = (value = '') => String(value || '').trim().replace(/\/+$/, '');

const BASE = trimTrailingSlash(
  process.env.REACT_APP_COVERAGE_API_BASE_URL || 'http://localhost:8787'
);

/** Drops empty values and the UI's "All" sentinel, which the API treats as unset. */
function qs(params) {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== 'All') search.set(k, String(v));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

/** A dead API must degrade to demo data quickly, not hang the dashboard. */
const TIMEOUT_MS = 4000;

export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

async function request(path, init) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${BASE}${path}`, { ...init, signal: controller.signal });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(res.status, body.error ?? `${res.status} ${res.statusText}`);
    }
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

export const api = {
  health: () => request('/api/health'),

  coverageSummary: () => request('/api/coverage/summary'),

  requirements: (params = {}) => request(`/api/coverage/requirements${qs(params)}`),

  requirement: (idOrKey) => request(`/api/coverage/requirements/${encodeURIComponent(idOrKey)}`),

  statusDetail: (status) => request(`/api/coverage/status/${encodeURIComponent(status)}`),

  /** Requirements joined to the BRD, test cases, and captures behind them. */
  traceability: (params = {}) => request(`/api/coverage/traceability${qs(params)}`),

  sourceSummary: () => request('/api/sources/summary'),

  sources: (kind) => request(`/api/sources${kind ? `?kind=${kind}` : ''}`),

  /** Drill-down behind one INPUT SOURCES tile. */
  sourceKindDetail: (kind) => request(`/api/sources/kind/${kind}`),

  /** All four tiles in one call. */
  sourceDetails: () => request('/api/sources/details'),

  /** One artifact with everything it produced. */
  artifact: (sourceId) => request(`/api/sources/${encodeURIComponent(sourceId)}`),

  // ── downloads ─────────────────────────────────────────────────────────────

  /** CSV links, for an <a download> — the browser fetches these, not us. */
  exportUrls: (params = {}) => ({
    requirements: `${BASE}/api/coverage/export.csv${qs(params)}`,
    traceability: `${BASE}/api/coverage/traceability.csv${qs(params)}`,
  }),

  // ── ingest ────────────────────────────────────────────────────────────────

  uploadBrd: (file) => {
    const form = new FormData();
    form.append('file', file);
    return request('/api/sources/brd', { method: 'POST', body: form });
  },

  uploadTestSheet: (file) => {
    const form = new FormData();
    form.append('file', file);
    return request('/api/sources/test-sheet', { method: 'POST', body: form });
  },

  uploadScreenCaptures: (files) => {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    return request('/api/sources/screen-capture', { method: 'POST', body: form });
  },

  recompute: () => request('/api/coverage/recompute', { method: 'POST' }),

  // ── manual link overrides ─────────────────────────────────────────────────

  /** Override the matcher. Manual links survive a later recompute. */
  linkArtifact: (requirement, artifact) =>
    request(`/api/coverage/requirements/${encodeURIComponent(requirement)}/links`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(artifact),
    }),

  unlinkArtifact: (requirement, artifact) =>
    request(`/api/coverage/requirements/${encodeURIComponent(requirement)}/links${qs(artifact)}`, {
      method: 'DELETE',
    }),
};
